import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  ArrowLeft,
  UserPlus,
  Copy,
  CheckCircle,
  WhatsappLogo,
  EnvelopeSimple,
  MapPin,
  Buildings,
} from '@phosphor-icons/react';

const API = '/api';

const EMPTY_FORM = {
  name: '',
  email: '',
  whatsapp: '',
  phone: '',
  city: '',
  enterprise_ids: [],
  active: true,
};

const NovoCorretor = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [enterprises, setEnterprises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/enterprises`, { withCredentials: true })
      .then(({ data }) => setEnterprises(data || []))
      .catch(() => {
        toast.error('Erro ao carregar empreendimentos');
      });
  }, []);

  const toggleEnterprise = (enterpriseId) => {
    setForm((current) => {
      const selected = current.enterprise_ids.includes(enterpriseId);

      return {
        ...current,
        enterprise_ids: selected
          ? current.enterprise_ids.filter((id) => id !== enterpriseId)
          : [...current.enterprise_ids, enterpriseId],
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setInviteResult(null);

    try {
      const { data } = await axios.post(
        `${API}/admin/brokers`,
        form,
        { withCredentials: true }
      );

      setInviteResult(data);
      toast.success('Corretor criado com sucesso');
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
        'Erro ao cadastrar corretor'
      );
    } finally {
      setLoading(false);
    }
  };

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(
        inviteResult?.invite_message || ''
      );

      toast.success('Convite copiado');
    } catch {
      toast.error('Não foi possível copiar automaticamente');
    }
  };

  const createAnother = () => {
    setInviteResult(null);
    setForm(EMPTY_FORM);
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-indigo-100 flex items-center justify-center">
              <UserPlus
                size={32}
                weight="duotone"
                className="text-indigo-600"
              />
            </div>

            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                Novo Corretor
              </h1>
              <p className="text-slate-600 mt-1">
                Crie o acesso e gere o convite para o primeiro login.
              </p>
            </div>
          </div>

          <Link
            to="/gestao-corretores"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl border border-slate-300 bg-white text-slate-700 font-medium"
          >
            <ArrowLeft size={18} />
            Voltar
          </Link>
        </div>

        {!inviteResult ? (
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field
                label="Nome completo"
                icon={<UserPlus size={18} />}
              >
                <input
                  required
                  value={form.name}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      name: event.target.value,
                    })
                  }
                  className="w-full rounded-xl border border-slate-300 px-4 py-3"
                  placeholder="Nome do corretor"
                />
              </Field>

              <Field
                label="E-mail de acesso"
                icon={<EnvelopeSimple size={18} />}
              >
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      email: event.target.value,
                    })
                  }
                  className="w-full rounded-xl border border-slate-300 px-4 py-3"
                  placeholder="corretor@empresa.com"
                />
              </Field>

              <Field
                label="WhatsApp"
                icon={<WhatsappLogo size={18} />}
              >
                <input
                  required
                  value={form.whatsapp}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      whatsapp: event.target.value,
                      phone: event.target.value,
                    })
                  }
                  className="w-full rounded-xl border border-slate-300 px-4 py-3"
                  placeholder="+5577999999999"
                />

                <p className="text-xs text-slate-500 mt-2">
                  Informe com código do país e DDD.
                </p>
              </Field>

              <Field
                label="Cidade principal"
                icon={<MapPin size={18} />}
              >
                <input
                  value={form.city}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      city: event.target.value,
                    })
                  }
                  className="w-full rounded-xl border border-slate-300 px-4 py-3"
                  placeholder="Petrolina"
                />
              </Field>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <Buildings size={20} className="text-indigo-600" />
                <h2 className="font-bold text-slate-900">
                  Empreendimentos iniciais
                </h2>
              </div>

              {enterprises.length === 0 ? (
                <p className="text-sm text-slate-500">
                  Nenhum empreendimento cadastrado.
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {enterprises.map((enterprise) => {
                    const selected =
                      form.enterprise_ids.includes(enterprise.id);

                    return (
                      <button
                        key={enterprise.id}
                        type="button"
                        onClick={() =>
                          toggleEnterprise(enterprise.id)
                        }
                        className={`text-left p-4 rounded-xl border ${
                          selected
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-slate-200'
                        }`}
                      >
                        <p className="font-semibold text-slate-900">
                          {enterprise.name}
                        </p>

                        <p className="text-sm text-slate-500 mt-1">
                          {enterprise.city || 'Cidade não informada'}
                        </p>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <label className="flex items-center justify-between gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
              <div>
                <p className="font-semibold text-slate-900">
                  Ativar conta imediatamente
                </p>
                <p className="text-sm text-slate-500">
                  O corretor poderá acessar assim que receber o convite.
                </p>
              </div>

              <input
                type="checkbox"
                checked={form.active}
                onChange={(event) =>
                  setForm({
                    ...form,
                    active: event.target.checked,
                  })
                }
                className="w-5 h-5"
              />
            </label>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-60"
              >
                <UserPlus size={20} />
                {loading
                  ? 'Criando acesso...'
                  : 'Criar corretor e convite'}
              </button>
            </div>
          </form>
        ) : (
          <div className="bg-white rounded-2xl border border-emerald-200 overflow-hidden">
            <div className="p-6 bg-emerald-50 border-b border-emerald-200">
              <div className="flex items-center gap-3">
                <CheckCircle
                  size={32}
                  weight="fill"
                  className="text-emerald-600"
                />

                <div>
                  <h2 className="text-2xl font-bold text-emerald-900">
                    Corretor criado com sucesso
                  </h2>

                  <p className="text-emerald-700">
                    Copie o convite e envie diretamente ao corretor.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoBox
                  label="Nome"
                  value={inviteResult.broker?.name}
                />

                <InfoBox
                  label="E-mail"
                  value={inviteResult.broker?.email}
                />

                <InfoBox
                  label="Senha temporária"
                  value={inviteResult.temporary_password}
                  highlight
                />

                <InfoBox
                  label="Status"
                  value="Convite pendente"
                />
              </div>

              <div>
                <p className="text-sm font-semibold text-slate-700 mb-2">
                  Mensagem de convite
                </p>

                <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed p-5 rounded-xl bg-slate-950 text-slate-100 overflow-x-auto">
                  {inviteResult.invite_message}
                </pre>
              </div>

              <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                A senha temporária é exibida somente agora. Guarde ou envie
                o convite antes de sair desta tela.
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-3">
                <button
                  type="button"
                  onClick={createAnother}
                  className="px-5 py-3 rounded-xl border border-slate-300 font-semibold text-slate-700"
                >
                  Cadastrar outro
                </button>

                <button
                  type="button"
                  onClick={copyInvite}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-indigo-600 text-white font-semibold"
                >
                  <Copy size={20} />
                  Copiar convite
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

const Field = ({ label, icon, children }) => (
  <div>
    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
      <span className="text-indigo-600">{icon}</span>
      {label}
    </label>

    {children}
  </div>
);

const InfoBox = ({ label, value, highlight = false }) => (
  <div
    className={`p-4 rounded-xl border ${
      highlight
        ? 'bg-indigo-50 border-indigo-300'
        : 'bg-slate-50 border-slate-200'
    }`}
  >
    <p className="text-xs uppercase text-slate-500">{label}</p>

    <p
      className={`font-bold mt-1 break-all ${
        highlight ? 'text-indigo-700 text-lg' : 'text-slate-900'
      }`}
    >
      {value || 'Não informado'}
    </p>
  </div>
);

export default NovoCorretor;
